import { getOfferPaymentState, isOffPlatformPayment } from '../types';
import type { OfferItem, OfferPayment } from '../types';
import { calculateOfferFees } from './offerFees';

export const OFFER_PAYMENT_POLL_MS = 10_000;
export const MIN_OFFER_PAYMENT_AMOUNT = 10;

export interface SessionPaymentLink {
  url: string;
  createdAt: string;
  amount?: number;
  milestoneId?: number | null;
}

export interface ActivePaymentLink {
  url: string;
  createdAt?: string;
  amount?: number;
  /** Total the payer is charged, including the processing fee, when the API returns it. */
  payerTotal?: number;
  milestoneIds: number[];
  status: string;
  used: boolean;
}

export function isPaidPaymentStatus(status?: string | null): boolean {
  return getOfferPaymentState({ status: status || '' }) === 'confirmed';
}

export function sortPaymentsNewestFirst<T extends { createdAt?: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bTime - aTime;
  });
}

function paymentMilestoneIds(payment?: OfferPayment, sessionMilestoneId?: number | null): number[] {
  if (payment?.milestoneIds && payment.milestoneIds.length > 0) return payment.milestoneIds;
  if (payment?.milestoneId != null) return [payment.milestoneId];
  if (sessionMilestoneId != null) return [sessionMilestoneId];
  return [];
}

/**
 * Latest unpaid Stripe payment link for the offer that still covers unpaid work.
 * Prefers a link generated in this browser session, then pending payments from
 * the API, then the offer-level link returned on acceptance.
 */
export function findActivePaymentLink(
  offer: Pick<OfferItem, 'payments' | 'stripePaymentLinkUrl' | 'milestones'>,
  session?: SessionPaymentLink | null,
): ActivePaymentLink | null {
  const payments = (offer.payments ?? []).filter((p) => !isOffPlatformPayment(p));
  const paidUrls = new Set(
    payments
      .filter((p) => p.stripePaymentLinkUrl && isPaidPaymentStatus(p.status))
      .map((p) => p.stripePaymentLinkUrl as string),
  );

  const coversUnpaidWork = (payment?: OfferPayment, sessionMilestoneId?: number | null) => {
    const ids = paymentMilestoneIds(payment, sessionMilestoneId);
    if (ids.length === 0) return true;
    return ids.some((id) => {
      const milestone = offer.milestones.find((m) => Number(m.id) === id);
      return milestone != null && milestone.status !== 'paid';
    });
  };

  const toLink = (
    url: string,
    payment?: OfferPayment,
    sessionLink?: SessionPaymentLink | null,
  ): ActivePaymentLink => {
    const status = payment?.status || 'pending';
    return {
      url,
      createdAt: payment?.createdAt || sessionLink?.createdAt,
      amount: payment?.amount ?? sessionLink?.amount,
      payerTotal: payment?.payerTotalAmount,
      milestoneIds: paymentMilestoneIds(payment, sessionLink?.milestoneId),
      status,
      used: isPaidPaymentStatus(status),
    };
  };

  if (session?.url && !paidUrls.has(session.url)) {
    const match = payments.find((p) => p.stripePaymentLinkUrl === session.url);
    if (
      (!match || !isPaidPaymentStatus(match.status)) &&
      coversUnpaidWork(match, session.milestoneId)
    ) {
      return toLink(session.url, match, session);
    }
  }

  const pendingWithUrl = sortPaymentsNewestFirst(
    payments.filter(
      (p) => p.stripePaymentLinkUrl && getOfferPaymentState(p) === 'pending' && coversUnpaidWork(p),
    ),
  );
  if (pendingWithUrl[0]?.stripePaymentLinkUrl) {
    return toLink(pendingWithUrl[0].stripePaymentLinkUrl, pendingWithUrl[0]);
  }

  if (offer.stripePaymentLinkUrl && !paidUrls.has(offer.stripePaymentLinkUrl)) {
    const match = payments.find((p) => p.stripePaymentLinkUrl === offer.stripePaymentLinkUrl);
    if ((!match || !isPaidPaymentStatus(match.status)) && coversUnpaidWork(match)) {
      return toLink(offer.stripePaymentLinkUrl, match);
    }
  }

  return null;
}

export function paymentLinkPayerTotal(
  link: Pick<ActivePaymentLink, 'amount' | 'payerTotal'>,
): number | undefined {
  if (link.payerTotal != null && Number.isFinite(link.payerTotal)) return link.payerTotal;
  if (link.amount != null && Number.isFinite(link.amount) && link.amount > 0) {
    return calculateOfferFees(link.amount).creatorTotal;
  }
  return undefined;
}

export function getOfferRemainingBalance(
  offer: Pick<OfferItem, 'remainingBalance' | 'price' | 'amountPaid'>,
): number {
  return offer.remainingBalance != null && Number.isFinite(offer.remainingBalance)
    ? offer.remainingBalance
    : Math.max(0, offer.price - (offer.amountPaid ?? 0));
}

export function isOfferRecipient(
  offer: Pick<OfferItem, 'creatorId' | 'recipientUserId' | 'recipientEmail'> & {
    conversationId?: string | null;
  },
  currentUserId: string,
  currentUserEmail: string,
  contextConversationId?: string | null,
): boolean {
  const isCreator =
    !!currentUserId && !!offer.creatorId && String(offer.creatorId) === currentUserId;
  if (isCreator) return false;
  if (offer.recipientUserId != null && String(offer.recipientUserId) === currentUserId) {
    return true;
  }
  const emailA = offer.recipientEmail?.trim().toLowerCase();
  const emailB = currentUserEmail?.trim().toLowerCase();
  if (emailA && emailB && emailA === emailB) {
    return true;
  }
  if (
    contextConversationId &&
    offer.conversationId &&
    contextConversationId === offer.conversationId &&
    !!currentUserId
  ) {
    return true;
  }
  return false;
}

export type OfferParty = 'creator' | 'recipient';

export interface PaymentRoles {
  isCreator: boolean;
  isRecipient: boolean;
  /** Party allowed to call `payment-links` for this offer. */
  linkGenerator: OfferParty;
  /** Party that pays the Stripe link. */
  payer: OfferParty;
  isLinkGenerator: boolean;
  isPayer: boolean;
}

/**
 * Single source of truth for who generates the Stripe link and who pays it.
 * Gig offers: the recipient requests payment and the creator pays.
 * Standalone offers: the creator sends the link and the recipient pays.
 */
export function getPaymentRoles(
  offer: Pick<OfferItem, 'creatorId' | 'recipientUserId' | 'recipientEmail' | 'gigUuid'> & {
    conversationId?: string | null;
  },
  currentUserId: string,
  currentUserEmail: string,
  contextConversationId?: string | null,
): PaymentRoles {
  const isCreator =
    !!currentUserId && !!offer.creatorId && String(offer.creatorId) === currentUserId;
  const isRecipient = isOfferRecipient(
    offer,
    currentUserId,
    currentUserEmail,
    contextConversationId,
  );
  const linkGenerator: OfferParty = offer.gigUuid ? 'recipient' : 'creator';
  const payer: OfferParty = linkGenerator === 'recipient' ? 'creator' : 'recipient';
  const party: OfferParty | null = isCreator ? 'creator' : isRecipient ? 'recipient' : null;
  return {
    isCreator,
    isRecipient,
    linkGenerator,
    payer,
    isLinkGenerator: party === linkGenerator,
    isPayer: party === payer,
  };
}

const RECORDED_SETTLEMENTS_KEY = 'musicade:recorded-settlements';

function readRecordedSettlements(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECORDED_SETTLEMENTS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

/** Remembers settlements this browser recorded, since the API doesn't say who proposed them. */
export function rememberRecordedSettlement(offerId: string, paymentId: number) {
  if (typeof window === 'undefined') return;
  const key = `${offerId}:${paymentId}`;
  const next = [key, ...readRecordedSettlements().filter((k) => k !== key)].slice(0, 50);
  try {
    window.localStorage.setItem(RECORDED_SETTLEMENTS_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable; the backend still enforces the counterparty rule.
  }
}

export function findOffPlatformPayments(offer: Pick<OfferItem, 'payments'>): OfferPayment[] {
  return sortPaymentsNewestFirst((offer.payments ?? []).filter(isOffPlatformPayment));
}

export interface PendingSettlement {
  payment: OfferPayment;
  /** True when the current user is the counterparty who must confirm or reject. */
  waitingOnMe: boolean;
}

export function findPendingSettlement(
  offer: Pick<OfferItem, 'id' | 'payments'>,
  currentUserId: string,
): PendingSettlement | null {
  const payment = findOffPlatformPayments(offer).find((p) => getOfferPaymentState(p) === 'pending');
  if (!payment) return null;
  if (payment.proposedById != null && currentUserId) {
    return { payment, waitingOnMe: String(payment.proposedById) !== currentUserId };
  }
  const recordedByMe = readRecordedSettlements().includes(`${offer.id}:${payment.id}`);
  return { payment, waitingOnMe: !recordedByMe };
}

/**
 * Poll while either party is waiting on the other: the payer for a link, the
 * link generator for payment, or anyone for a settlement confirmation.
 */
export function offerPaymentPollInterval(
  offer: OfferItem | undefined,
  currentUserId: string,
  currentUserEmail: string,
): number | false {
  if (!offer) return false;
  if (offer.status !== 'accepted') return false;
  if (offer.paymentStatus === 'paid') return false;
  if (getOfferRemainingBalance(offer) <= 0.001) return false;
  const roles = getPaymentRoles(offer, currentUserId, currentUserEmail);
  if (!roles.isCreator && !roles.isRecipient) return false;
  if (findPendingSettlement(offer, currentUserId)) return OFFER_PAYMENT_POLL_MS;
  const hasLink = !!findActivePaymentLink(offer);
  if (roles.isPayer && !hasLink) return OFFER_PAYMENT_POLL_MS;
  if (roles.isLinkGenerator && hasLink) return OFFER_PAYMENT_POLL_MS;
  return false;
}
